import {
  type CSSRSPromptResult,
  type MeasureBundleObject,
  type MeasureBundleSourceType,
} from '@grow-therapy-team/seedling-components';

import { CSSRS_PROMPT_CALL_TO_ACTION, KaiserIntakeCSSRPromptText } from './constants';

export type ChildCSSRElevatedScoreType = 'depression' | 'anxiety' | 'both';

export const getAdultCSSRPromptText = (patientName: string) => ({
  HEADING: 'Consider administering the C-SSRS',
  BODY: `${patientName} screened positive for suicidal thinking (question 9 on PHQ9). As clinical best practice, consider further evaluation using the Columbia-Suicide Severity Rating Scale (C-SSRS).`,
  CALL_TO_ACTION: CSSRS_PROMPT_CALL_TO_ACTION,
});

export const getChildCSSRPromptText = (patientName: string, elevatedScoreType: ChildCSSRElevatedScoreType) => {
  const possessive = `${patientName}’s`;

  if (elevatedScoreType === 'both') {
    return {
      HEADING: 'Severe depression and anxiety: Consider administering the C-SSRS',
      BODY: `${possessive} PROMIS scores indicate severe depression and anxiety symptoms (T-Score ≥ 65). As clinical best practice, consider further evaluation using the Columbia-Suicide Severity Rating Scale (C-SSRS).`,
      CALL_TO_ACTION: CSSRS_PROMPT_CALL_TO_ACTION,
    };
  }

  return {
    HEADING: `Severe ${elevatedScoreType} score: Consider administering the C-SSRS`,
    BODY: `${possessive} PROMIS-${elevatedScoreType} score indicates severe ${elevatedScoreType} symptoms (T-Score ≥ 65). As clinical best practice, consider further evaluation using the Columbia-Suicide Severity Rating Scale (C-SSRS).`,
    CALL_TO_ACTION: CSSRS_PROMPT_CALL_TO_ACTION,
  };
};

export type BundleCompletionState = {
  state: 'completed' | 'partially_completed' | 'incomplete';
  lastCompletion: Date | null;
};

export function sortMeasureBundles(
  measureBundles: MeasureBundleObject[],
  dateField: keyof MeasureBundleObject = 'sentAt',
) {
  return [...measureBundles].sort((a: MeasureBundleObject, b: MeasureBundleObject) => {
    return new Date(b[dateField] || 0).getTime() - new Date(a[dateField] || 0).getTime();
  });
}

export function getBundleCompletionState(bundles: MeasureBundleObject[]): BundleCompletionState {
  if (!bundles || bundles.length === 0) {
    return { state: 'incomplete', lastCompletion: null };
  }

  const mostRecentBundle = bundles[0]!;
  if (mostRecentBundle.completedAt) {
    return {
      state: 'completed',
      lastCompletion: new Date(mostRecentBundle.completedAt),
    };
  }

  const completedBundleItem = mostRecentBundle.measureBundleItems.find((item) => item?.measureResponse?.completedAt);
  if (completedBundleItem) {
    return {
      state: 'partially_completed',
      lastCompletion: new Date(completedBundleItem.measureResponse!.completedAt!),
    };
  }

  const lastCompletion = bundles.find((bundle) => bundle?.completedAt)?.completedAt;
  return {
    state: 'incomplete',
    lastCompletion: lastCompletion ? new Date(lastCompletion) : null,
  };
}

/**
 * Returns the measure bundle and measure bundle item that corresponds to the
 * given measure response short ID.
 */
export function findMeasureBundleInfoByResponseShortId(
  measureBundles: MeasureBundleObject[],
  measureResponseShortId: string,
) {
  for (const measureBundle of measureBundles) {
    for (const measureBundleItem of measureBundle.measureBundleItems) {
      if (measureBundleItem?.measureResponse?.measureResponseShortId === measureResponseShortId) {
        return { measureBundle, measureBundleItem };
      }
    }
  }
  return {};
}

/** Mirrors seedling `CSSRSTriggerSource` (package re-exports enums as types only). */
const CSSRS_TRIGGER = {
  PHQ9: 'PHQ9',
  KAISER_INTAKE: 'KAISER_INTAKE',
  PROMIS_ANXIETY: 'PROMIS_ANXIETY',
  PROMIS_DEPRESSION: 'PROMIS_DEPRESSION',
  PROMIS_BOTH: 'PROMISE_BOTH',
} as const;

/** Mirrors seedling `CSSRSType`. */
const CSSRS_TYPE = {
  Adult: 'adult',
  Child: 'child',
} as const;

const PROMIS_TRIGGERS: readonly string[] = [
  CSSRS_TRIGGER.PROMIS_ANXIETY,
  CSSRS_TRIGGER.PROMIS_DEPRESSION,
  CSSRS_TRIGGER.PROMIS_BOTH,
];

export type CSSRSPromptToastCopy = {
  heading: string;
  body: string;
  variant: 'alert' | 'info';
  callToAction: string;
};

/**
 * Maps prompt context to the GraphQL `cSsrsSourceType` sent with C-SSRS submissions.
 * Kaiser intake and anytime (provider-initiated) flows use dedicated bundle source types;
 * screening follow-ups (PHQ-9 / PROMIS) use `PREVIOUS_RESPONSE` with `previousResponseShortId`.
 */
export function getCSSRSSubmissionSourceType(result: CSSRSPromptResult): MeasureBundleSourceType {
  // `CSSRSTriggerSource` is type-only from seedling; compare to the same string values the API uses.
  if (result.triggerSource === 'KAISER_INTAKE') {
    return 'KAISER_INTAKE' as MeasureBundleSourceType;
  }

  const isScreeningFollowUp =
    Boolean(result.previousResponseShortId) &&
    result.triggerSource !== null &&
    (result.triggerSource === 'PHQ9' ||
      result.triggerSource === 'PROMIS_ANXIETY' ||
      result.triggerSource === 'PROMIS_DEPRESSION' ||
      result.triggerSource === 'PROMISE_BOTH');

  if (isScreeningFollowUp) {
    return 'PREVIOUS_RESPONSE' as MeasureBundleSourceType;
  }

  return 'ANYTIME_C_SSRS' as MeasureBundleSourceType;
}

export function getCSSRSPromptToastCopy(
  result: CSSRSPromptResult,
  patientDisplayName: string,
  kaiserGate: boolean,
  childGate: boolean,
): CSSRSPromptToastCopy | null {
  if (!result.shouldPrompt) {
    return null;
  }

  if (result.triggerSource === CSSRS_TRIGGER.KAISER_INTAKE) {
    if (!kaiserGate) {
      return null;
    }
    return {
      heading: KaiserIntakeCSSRPromptText.HEADING,
      body: KaiserIntakeCSSRPromptText.BODY,
      variant: 'info',
      callToAction: KaiserIntakeCSSRPromptText.CALL_TO_ACTION,
    };
  }

  if (result.cssrsType === CSSRS_TYPE.Child && result.triggerSource && PROMIS_TRIGGERS.includes(result.triggerSource)) {
    if (!childGate) {
      return null;
    }
    const elevatedScoreType =
      result.triggerSource === CSSRS_TRIGGER.PROMIS_ANXIETY
        ? 'anxiety'
        : result.triggerSource === CSSRS_TRIGGER.PROMIS_DEPRESSION
          ? 'depression'
          : 'both';
    const text = getChildCSSRPromptText(patientDisplayName, elevatedScoreType);
    return {
      heading: text.HEADING,
      body: text.BODY,
      variant: 'alert',
      callToAction: text.CALL_TO_ACTION,
    };
  }

  if (result.triggerSource === CSSRS_TRIGGER.PHQ9 && result.cssrsType === CSSRS_TYPE.Adult) {
    const text = getAdultCSSRPromptText(patientDisplayName);
    return {
      heading: text.HEADING,
      body: text.BODY,
      variant: 'alert',
      callToAction: text.CALL_TO_ACTION,
    };
  }

  return null;
}
